import { formatMoneyShort, type CostBarDto } from '@nsb/core';

/**
 * The money bar's drawing as data (design study 04, revision 5, with the price
 * taken out on 2026-10-07), kept out of the component so it can be tested: how
 * far the scale reaches, the named lines on it, and where each name goes.
 */

export type MarkTone = 'ink' | 'ink-2' | 'ink-3' | 'alert';

/** A line on the bar, named at its own top. */
export interface BarMark {
  /** Where it stands, in cents along the bar. */
  at: number;
  text: string;
  tone: MarkTone;
  /** `dotted`: a line not yet settled (the contingency inside the budget, a budget waiting). */
  kind: 'line' | 'dotted';
  /** The last word of the name, when it opens something: "pending" opens the budget's answer. */
  link?: { word: string; href: string };
}

export interface PlacedMark {
  mark: BarMark;
  /** Pixels from the bar's start. */
  x: number;
  /** 0 is the row nearest the bar. */
  row: number;
  anchor: 'start' | 'end';
}

/** How far the bar's scale reaches: billed or money in, the budget and one waiting, whichever is furthest. */
export function scaleOf(data: CostBarDto): number {
  const bar = data.bar;
  if (bar?.ended) return Math.max(bar.totalCents, 1);
  return Math.max(bar?.totalCents ?? 0, data.ceiling?.totalCents ?? 0, data.pendingBudget?.totalCents ?? 0, 1);
}

/** The named lines: the budget and where its contingency begins, and a budget waiting; on a job that ended, what goes back. */
export function marksOf(data: CostBarDto, budgetHref: string | null): BarMark[] {
  const bar = data.bar;
  if (bar?.ended) {
    return bar.aheadCents > 0 ? [{ at: bar.moneyInCents, text: `To give back ${formatMoneyShort(bar.aheadCents)}`, tone: 'alert', kind: 'line' }] : [];
  }
  const marks: BarMark[] = [];
  const ceiling = data.ceiling;
  if (ceiling) {
    // Where the contingency held inside the budget begins, dotted and named (A-B).
    if (ceiling.contingencyCents > 0) marks.push({ at: ceiling.workCents, text: 'Contingency', tone: 'ink-3', kind: 'dotted' });
    marks.push({ at: ceiling.totalCents, text: `Budget ${formatMoneyShort(ceiling.totalCents)}`, tone: 'ink', kind: 'line' });
  }
  const waiting = data.pendingBudget;
  if (waiting) {
    marks.push({
      at: waiting.totalCents,
      text: `Budget ${formatMoneyShort(waiting.totalCents)} · pending`,
      tone: 'ink-2',
      kind: 'dotted',
      ...(budgetHref ? { link: { word: 'pending', href: budgetHref } } : {}),
    });
  }
  return marks;
}

/** About how wide a name is at the bar's 10.5px Inter, when the page cannot measure it. */
export const roughWidth = (text: string): number => text.length * 6 + 2;

/**
 * Each name at the top of its own line, beside it, staggered up a row when it
 * would touch another; a line never runs through a name below it, and a name
 * never sits over a line that rises past it. No leaders: the line is the
 * leader (Jason, 2026-10-04: "the little lables on the bar, the lines look
 * fucked up"). Laid left to right and right to left, keeping whichever names
 * more lines in fewer rows (two lines close together then name outward). A
 * name that fits nowhere in five rows is left off; the legend still says it.
 */
export function placeMarks(marks: readonly BarMark[], px: (cents: number) => number, width: number, measure: (text: string) => number = roughWidth): PlacedMark[] {
  const lay = (order: readonly BarMark[]): PlacedMark[] => {
    const boxes: { x0: number; x1: number; row: number }[] = [];
    const rises: { x: number; row: number }[] = [];
    const out: PlacedMark[] = [];
    for (const mark of order) {
      const x = px(mark.at);
      const tw = measure(mark.text);
      let done = false;
      for (let row = 0; row < 5 && !done; row++) {
        for (const anchor of ['start', 'end'] as const) {
          const [x0, x1] = anchor === 'start' ? [x + 4, x + 4 + tw] : [x - 4 - tw, x - 4];
          if (x0 < -2 || x1 > width + 2) continue;
          if (boxes.some((b) => b.row === row && !(x1 + 8 < b.x0 || x0 - 8 > b.x1))) continue;
          if (boxes.some((b) => b.row < row && b.x0 - 3 <= x && x <= b.x1 + 3)) continue;
          if (rises.some((r) => r.row >= row && x0 - 3 <= r.x && r.x <= x1 + 3)) continue;
          boxes.push({ x0, x1, row });
          rises.push({ x, row });
          out.push({ mark, x, row, anchor });
          done = true;
          break;
        }
      }
    }
    return out;
  };
  const rightward = [...marks].sort((a, b) => a.at - b.at);
  const a = lay(rightward);
  const b = lay([...rightward].reverse());
  const rows = (p: PlacedMark[]) => (p.length ? Math.max(...p.map((m) => m.row)) : -1);
  return b.length > a.length || (b.length === a.length && rows(b) < rows(a)) ? b : a;
}
